/**
 * Format date input as MM/DD/YYYY
 * @param {string} value - Input value
 * @returns {string} - Formatted date string
 */
export const formatDateInput = (value) => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply MM/DD/YYYY formatting
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  }
};

/**
 * Format SSN input as XXX-XX-XXXX
 * @param {string} value - Input value
 * @returns {string} - Formatted SSN string
 */
export const formatSSN = (value) => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply XXX-XX-XXXX formatting
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 5) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  }
};

/**
 * Format phone number input as (XXX) XXX-XXXX
 * @param {string} value - Input value
 * @returns {string} - Formatted phone number string
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply (XXX) XXX-XXXX formatting
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  } else {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

/**
 * Convert MM/DD/YYYY to YYYY-MM-DD for storage
 * @param {string} dateString - Date in MM/DD/YYYY format
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const convertToISO = (dateString) => {
  if (!dateString || dateString.length < 10) return '';
  
  const parts = dateString.split('/');
  if (parts.length !== 3) return '';
  
  const [month, day, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Convert YYYY-MM-DD to MM/DD/YYYY for display
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {string} - Date in MM/DD/YYYY format
 */
export const convertFromISO = (isoDate) => {
  if (!isoDate) return '';
  
  const parts = isoDate.split('-');
  if (parts.length !== 3) return '';
  
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
};

/**
 * Display date value - handles both MM/DD/YYYY and YYYY-MM-DD formats
 * @param {string} value - Date value from form data
 * @returns {string} - Date in MM/DD/YYYY format for display
 */
export const displayDateValue = (value) => {
  if (!value) return '';
  
  // If it looks like ISO format (YYYY-MM-DD), convert it
  if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return convertFromISO(value);
  }
  
  // Otherwise, return as-is (partial or complete MM/DD/YYYY)
  return value;
};

/**
 * Validate date format MM/DD/YYYY
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid
 */
export const isValidDate = (dateString) => {
  if (!dateString || dateString.length !== 10) return false;
  
  const parts = dateString.split('/');
  if (parts.length !== 3) return false;
  
  const [month, day, year] = parts.map(Number);
  
  // Basic validation
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1900 || year > 2100) return false;
  
  // Check if date is valid
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
};
